import { useApi } from 'providers/ApiProvider';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useQuery } from 'react-query';
import {
  generateAllergiesQuery,
  generateDietsQuery,
} from 'Utils/helpers/queryGenerators';
import { DietPreference } from 'interfaces/DietPreferenceInterface';
import { setUserProperties, logEvent } from 'firebase/analytics';
import { analytics } from 'firebase';
import { auth } from 'firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
  getUserPreferences,
  updateUserPreferences,
} from 'services/preference.service';
import { recordUserPreferenceChange } from 'services/restaurant-analytics.service';
import { useRestaurantContext } from './RestaurantContextProvider';

const DietaryPreferenceContext = createContext({} as any);

const FILTER_TARGET_RESTAURANT_SLUGS = ['bci_marines', 'army_dinex'];
const TARGET_KEYS = [
  'cal_lt_300',
  'cal_300_500',
  'cal_gt_500',
  'high_protein',
  'low_fat',
];
const CALORIE_KEY_MIGRATIONS: { [key: string]: string } = {
  cal_low: 'cal_lt_300',
  cal_moderate: 'cal_300_500',
  cal_high: 'cal_gt_500',
};

interface Props {
  children: ReactNode;
}

export const DietaryContextProvider = ({ children }: Props) => {
  const api = useApi();

  const [user, isUserLoading] = useAuthState(auth);
  const { currentRestaurantId, restaurantName, isRestaurantLoaded } =
    useRestaurantContext();

  const setLocal = (key: string, value: string) => {
    localStorage.setItem(key, value);
  };

  const getLocal = (key: string) => {
    const data = localStorage.getItem(key);
    if (data) {
      try {
        return JSON.parse(data);
      } catch (e) {
        return data;
      }
    } else {
      return [];
    }
  };

  const [selectedAllergies, setSelectedAllergies] = useState<string[]>(
    getLocal('selectedAllergies')
  );
  const [selectedDiets, setSelectedDiets] = useState<string[]>(
    getLocal('selectedDiets')
  );
  const [username, setUsername] = useState(getLocal('username'));

  const [allergyKeyToName, setAllergyKeyToName] = useState(
    {} as { [key: string]: string }
  );

  const [dietKeyToName, setDietKeyToName] = useState(
    {} as { [key: string]: string }
  );

  const {
    status: _allergiesStatus,
    error: _allergiesError,
    data: allergies,
    isLoading: _isLoadingAllergies,
  } = useQuery(generateAllergiesQuery(), async () => {
    return api.getAllergies();
  });

  const {
    status: _dietsStatus,
    error: _dietsError,
    data: diets,
    isLoading: _isLoadingDiets,
  } = useQuery([generateDietsQuery(), currentRestaurantId], async () => {
    const data = await api.getDiets();

    const isExplorerMode = window.location.pathname.includes('/explore');
    const isTargetRestaurant =
      currentRestaurantId &&
      FILTER_TARGET_RESTAURANT_SLUGS.includes(currentRestaurantId.toLowerCase());

    let filtered = (data || []).filter(
      (x: any) => !['slow_carb', 'clean_program'].includes(x.key)
    );

    // NOTE: for 07/19/2026 demo, only target restaurant should include new goals in explorer mode
    if (isExplorerMode && !isTargetRestaurant) {
      filtered = filtered.filter((x: any) => !TARGET_KEYS.includes(x.key));
    }

    return filtered;
  });

  useEffect(() => {
    if (isUserLoading) {
      return;
    }

    if (user) {
      getUserPreferences(user.uid, 'selectedAllergies').then((data) => {
        setSelectedAllergies(data);
        // Analytics: Log pre-selected allergies
        if (data && data.length > 0 && currentRestaurantId && restaurantName) {
          data.forEach((key: string) => {
            logEvent(analytics, 'dietary_preference_toggled', {
              user_id: user.uid,
              restaurant_id: currentRestaurantId,
              restaurant_name: restaurantName,
              preference_category: 'allergy',
              preference_key: key,
              action: 'added',
              source: 'pre_selected'
            });
          });
        }
      });

      getUserPreferences(user.uid, 'selectedDiets').then((data) => {
        setSelectedDiets(data);
        // Analytics: Log pre-selected diets
        if (data && data.length > 0 && currentRestaurantId && restaurantName) {
          data.forEach((key: string) => {
            logEvent(analytics, 'dietary_preference_toggled', {
              user_id: user.uid,
              restaurant_id: currentRestaurantId,
              restaurant_name: restaurantName,
              preference_category: 'diet',
              preference_key: key,
              action: 'added',
              source: 'pre_selected'
            });
          });
        }
      });
    } else {
      setSelectedAllergies(getLocal('selectedAllergies'));
      setSelectedDiets(getLocal('selectedDiets'));
    }
  }, [user, isUserLoading]);

  useEffect(() => {
    setAllergyKeyToName(
      ((allergies ?? []) as DietPreference[]).reduce((obj, allergy) => {
        obj[allergy.key] = allergy.name;
        return obj;
      }, {} as { [key: string]: string })
    );
  }, [allergies]);

  useEffect(() => {
    setDietKeyToName(
      ((diets ?? []) as DietPreference[]).reduce((obj, diet) => {
        obj[diet.key] = diet.name;
        return obj;
      }, {} as { [key: string]: string })
    );
  }, [diets]);

  useEffect(() => {
    // Accept both the temporary calorie keys and the long-standing keys from
    // saved browser/Firebase preferences. The original keys are canonical.
    const normalizedDiets = Array.from(
      new Set(
        selectedDiets.map((key) => CALORIE_KEY_MIGRATIONS[key] || key)
      )
    );

    if (normalizedDiets.join(',') !== selectedDiets.join(',')) {
      setLocal('selectedDiets', JSON.stringify(normalizedDiets));
      setSelectedDiets(normalizedDiets);
    }
  }, [selectedDiets]);

  useEffect(() => {
    const isExplorerMode = window.location.pathname.includes('/explore');
    const isTargetRestaurant =
      currentRestaurantId &&
      FILTER_TARGET_RESTAURANT_SLUGS.includes(currentRestaurantId.toLowerCase());

    // These diet goals are only available at the BCI locations. Since dietary
    // preferences are shared across restaurants, discard those BCI-only keys
    // when a customer opens another explorer restaurant.
    if (!isExplorerMode || !currentRestaurantId || isTargetRestaurant) {
      return;
    }

    const cleanedDiets = selectedDiets.filter(
      (key) => !TARGET_KEYS.includes(key)
    );

    if (cleanedDiets.length !== selectedDiets.length) {
      setLocal('selectedDiets', JSON.stringify(cleanedDiets));
      setSelectedDiets(cleanedDiets);
    }
  }, [currentRestaurantId, selectedDiets]);

  const toggleSelectedAllergy = (key: string) => {
    if (!selectedAllergies) {
      return;
    }

    const isRemoving = selectedAllergies.includes(key);
    let data: string[];

    if (isRemoving) {
      data = selectedAllergies.filter((k) => k !== key);
      setLocal('selectedAllergies', JSON.stringify(data));
      setSelectedAllergies(data);
    } else {
      data = [...selectedAllergies, key];
      setLocal('selectedAllergies', JSON.stringify(data));
      setSelectedAllergies(data);
    }

    // Log atomic Google Analytics event for individual allergy change
    if (user && currentRestaurantId && restaurantName) {
      logEvent(analytics, 'dietary_preference_toggled', {
        user_id: user.uid,
        restaurant_id: currentRestaurantId,
        restaurant_name: restaurantName,
        preference_category: 'allergy',
        preference_key: key,
        preference_name: allergyKeyToName[key] || key,
        action: isRemoving ? 'removed' : 'added',
        total_allergies: data.length,
        total_diets: (selectedDiets || []).length,
        current_allergies: data.join(','),
        current_diets: (selectedDiets || []).join(','),
      });
    }
  };

  const setUsernameHandler = (val: string) => {
    setLocal('username', val);
    setUsername(val);
  };

  useEffect(() => {
    if (selectedAllergies) {
      setUserProperties(analytics, {
        selectedAllergies,
      });
    }
    if (user) {
      updateUserPreferences(user.uid, 'selectedAllergies', selectedAllergies);
    }

    // Track allergy changes for restaurant analytics
    if (
      user &&
      currentRestaurantId &&
      restaurantName &&
      isRestaurantLoaded &&
      selectedAllergies.length > 0
    ) {
      recordUserPreferenceChange(
        user.uid,
        currentRestaurantId,
        restaurantName,
        selectedDiets || [],
        selectedAllergies,
        'update'
      ).catch((error) => {
        console.error('Failed to record allergy preference change:', error);
      });
    }
  }, [
    selectedAllergies,
    user,
    currentRestaurantId,
    restaurantName,
    selectedDiets,
    isRestaurantLoaded,
  ]);

  useEffect(() => {
    if (selectedDiets) {
      setUserProperties(analytics, {
        selectedDiets,
      });
    }

    if (user) {
      updateUserPreferences(user.uid, 'selectedDiets', selectedDiets);
    }

    // Track diet changes for restaurant analytics
    if (
      user &&
      currentRestaurantId &&
      restaurantName &&
      isRestaurantLoaded &&
      selectedDiets.length > 0
    ) {
      recordUserPreferenceChange(
        user.uid,
        currentRestaurantId,
        restaurantName,
        selectedDiets,
        selectedAllergies || [],
        'update'
      ).catch((error) => {
        console.error('Failed to record diet preference change:', error);
      });
    }
  }, [
    selectedDiets,
    user,
    currentRestaurantId,
    restaurantName,
    selectedAllergies,
    isRestaurantLoaded,
  ]);

  const toggleSelectedDiet = (key: string) => {
    if (!selectedDiets) {
      return;
    }

    const isRemoving = selectedDiets.includes(key);
    let data: string[];

    if (isRemoving) {
      data = selectedDiets.filter((k) => k !== key);
      setLocal('selectedDiets', JSON.stringify(data));
      setSelectedDiets(data);
    } else {
      data = [...selectedDiets, key];
      setLocal('selectedDiets', JSON.stringify(data));
      setSelectedDiets(data);
    }

    // Log atomic Google Analytics event for individual diet change
    if (user && currentRestaurantId && restaurantName) {
      logEvent(analytics, 'dietary_preference_toggled', {
        user_id: user.uid,
        restaurant_id: currentRestaurantId,
        restaurant_name: restaurantName,
        preference_category: 'diet',
        preference_key: key,
        preference_name: dietKeyToName[key] || key,
        action: isRemoving ? 'removed' : 'added',
        total_diets: data.length,
        total_allergies: (selectedAllergies || []).length,
        current_diets: data.join(','),
        current_allergies: (selectedAllergies || []).join(','),
      });
    }
  };

  const resetAllergies = () => {
    setSelectedAllergies([]);
    setLocal('selectedAllergies', JSON.stringify([]));
  };

  const resetDiets = () => {
    setSelectedDiets([]);
    setLocal('selectedDiets', JSON.stringify([]));
  };

  return (
    <DietaryPreferenceContext.Provider
      value={{
        allergies,
        diets,
        username,
        selectedAllergies: selectedAllergies ?? [],
        selectedDiets: selectedDiets ?? [],
        allergyKeyToName,
        dietKeyToName,
        toggleSelectedAllergy,
        toggleSelectedDiet,
        setUsernameHandler,
        resetAllergies,
        resetDiets,
      }}
    >
      {children}
    </DietaryPreferenceContext.Provider>
  );
};

interface ContextProps {
  allergies: DietPreference[];
  diets: DietPreference[];
  username: string;
  selectedAllergies: string[];
  selectedDiets: string[];
  allergyKeyToName: { [key: string]: string };
  dietKeyToName: { [key: string]: string };
  toggleSelectedAllergy: (key: string) => void;
  toggleSelectedDiet: (key: string) => void;
  setUsernameHandler: (key: string) => void;
  resetAllergies: () => void;
  resetDiets: () => void;
}

export const useDietaryPreferences = () =>
  useContext<ContextProps>(DietaryPreferenceContext);
